import "./sentry";
import * as Sentry from "@sentry/react";
import { createRouter, parseSearchWith, RouterProvider } from "@tanstack/react-router";
import { StrictMode, useContext } from "react";
import ReactDOM from "react-dom/client";
import { routeTree } from "./router.tsx";
import "./styles.css";
import { ThemeProvider } from "next-themes";
import type { PostHogConfig } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator.tsx";
import { LoginTransitionProvider } from "@/components/application/login-transition/login-transition.tsx";
import { TooltipProvider } from "@/components/ui/tooltip.tsx";
import { AuthContext, AuthProvider } from "@/providers/auth-provider.tsx";

import { QueryClientProviderWrapper } from "@/providers/QueryClientProviderWrapper.tsx";
import { ToastProvider } from "@/providers/ToastProvider.tsx";

export const router = createRouter({
  routeTree,
  context: { isAuthenticated: undefined, user: undefined },
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  trailingSlash: "never",
  parseSearch: parseSearchWith((value) => {
    const parsed = JSON.parse(value);
    return typeof parsed === "number" && String(parsed) !== value ? value : parsed;
  }),
});
type AppRouter = typeof router;

// Handle trailing slash removal on initial load
if (typeof window !== "undefined") {
  const path = window.location.pathname;
  if (path.length > 1 && path.endsWith("/")) {
    const newPath = path.slice(0, -1) + window.location.search + window.location.hash;
    window.history.replaceState(null, "", newPath);
  }
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
const options: Partial<PostHogConfig> = {
  api_host: import.meta.env.VITE_POSTHOG_HOST,
  capture_pageview: true,
  person_profiles: "identified_only",
  capture_exceptions: true,
};

export const Router = ({ router }: { router: AppRouter }) => {
  // No useAuth as that accesses Router context
  const { isAuthenticated, user } = useContext(AuthContext);

  if (isAuthenticated === undefined)
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <LoadingIndicator />
      </div>
    );

  return <RouterProvider router={router} context={{ isAuthenticated, user }} />;
};

const rootElement = document.getElementById("app");
// biome-ignore lint/style/noNonNullAssertion: root
const root = ReactDOM.createRoot(rootElement!, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
});
root.render(
  <StrictMode>
    <QueryClientProviderWrapper>
      <PostHogProvider apiKey={import.meta.env.VITE_POSTHOG_API_KEY ?? ""} options={options}>
        <ThemeProvider defaultTheme="light" attribute="class">
          <ToastProvider>
            <TooltipProvider>
              <AuthProvider>
                <LoginTransitionProvider>
                  <Router router={router} />
                </LoginTransitionProvider>
              </AuthProvider>
            </TooltipProvider>
          </ToastProvider>
        </ThemeProvider>
      </PostHogProvider>
    </QueryClientProviderWrapper>
  </StrictMode>,
);
